import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import Modal, { IModalChildProps } from '../../../common/modal';
import {
    IAccount,
    IEscalateOrder,
    IOrderDetails,
} from '../../../../models/order';
import './modal-styles.css';
import { RootState } from '../../../../store';
import { connect } from 'react-redux';
import {
    loadOrder,
    escalateOrder,
    resetEscalationStatus,
} from '../../../../store/order/order-actions';
import { setIsBusy } from '../../../../store/api/api-status-action';
import TextInputLabelOptional from '../../../inputs/text-input-label-optional';
import { FieldValidation, validate } from '../../../../validation/validation';
import * as Validators from '../../../../validation/validators';
import SelectInputModal from '../../../inputs/select-input-modal';
import TextAreaLabelOptional from '../../../inputs/text-area-label-optional';
import { IOptions } from '../../../inputs/select-input';
import { gaEvent } from '../../../../hooks/ga-hook';

interface IEscalateModal extends IModalChildProps {
    order: IOrderDetails;
    loadOrder: (orderNumber: string) => Promise<void>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
    accountDisplay: (account: IAccount) => string;
    billDisplay: (account: IAccount) => string;
    shippingDisplay: (order: IOrderDetails) => string;
    escalateOrder: (order: IEscalateOrder) => Promise<void>;
    escalateResponse: string | null | undefined;
    resetEscalationStatus: () => void;
}

const EscalateModal = ({
    onAccept,
    onClose,
    order,
    loadOrder,
    setIsBusy,
    accountDisplay,
    billDisplay,
    shippingDisplay,
    escalateOrder,
    escalateResponse,
    resetEscalationStatus,
}: IEscalateModal) => {
    const intl = useIntl();
    type errors = {
        escalationReason?: '';
        customerInput?: '';
        contactInformation?: '';
        contactEmail?: '';
        contactName?: '';
    };
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(false);
    const [errorsState, setErrorsState] = useState<errors>({});
    const [selected, setSelected] = useState(0);
    const [confirmed, setConfirmed] = useState(false);
    const [orderType, setOrderType] = useState(order.orderType);
    const [options, setOptions] = useState<IOptions[]>([
        {
            value: 0,
            text: 'Please make a selection',
            disabled: true,
        },
        {
            value: 1,
            text: 'Package not received',
            disabled: false,
        },
        {
            value: 2,
            text: 'Damaged items received',
            disabled: false,
        },
    ]);
    const temp: string[] = [];
    const allSerials = order.lineItems.map((item) => item.serialNumbers);
    const flattenedArray = temp.concat(...allSerials);
    const ids = order.lineItems.map((item) => {
        return item.lineItemId!;
    });
    const [tempEscalateOrder, setTempEscalateOrder] = useState<IEscalateOrder>({
        escalationReason: '',
        orderNumber: order.orderNumber,
        orderType: orderType,
        dateShipped: order.shipDate,
        trackingNumber: order.trackingInfo
            ? order.trackingInfo.trackingNumber
            : null,
        billToAccount: order.billTo.accountNumber,
        shipToAccount: order.shipTo.accountNumber,
        patientName: order.patientName,
        serialNumbersReceived: flattenedArray,
        lineItemId: ids,
        customerInput: '',
        contactInformation: '',
        contactEmail: '',
        contactName: '',
    });
    useEffect(() => {
        if (!escalateResponse) return;

        if (escalateResponse === 'Failed') {
            setError(true);
        } else {
            setSuccess(true);
            escalateResponse = null;
        }
    }, [escalateResponse]);

    useEffect(() => {
        if (order.orderType !== 'Repair, Remake or L&D') {
            setOrderType('New Order');
        }
    }, []);
    useEffect(() => {
        return () => {
            setSuccess(false);
            setError(false);
        };
    }, []);

    const fieldValidations = [
        new FieldValidation('escalationReason', 'An escalation reason', [
            Validators.required(),
        ]),
        new FieldValidation('contactInformation', 'Phone number', [
            Validators.isPhone('Please enter a valid phone number'),
        ]),
        new FieldValidation('contactEmail', 'Contact Email', [
            Validators.required(),
            Validators.isEmail(tempEscalateOrder.contactEmail),
        ]),
        new FieldValidation('contactName', 'Contact name', [
            Validators.required(),
        ]),
        new FieldValidation('customerInput', 'Additional details', [
            Validators.required(
                intl.formatMessage(
                    {
                        id: 'Required_Field_Message',
                        defaultMessage: '{0} are required',
                    },
                    {
                        0: intl.formatMessage({
                            id: 'Additional_details',
                            defaultMessage: 'Additional details',
                        }),
                    }
                )
            ),
        ]),
    ];
    const onSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const choice = parseInt(e.target.value);
        setSelected(choice);
        const reason = options.find((r: IOptions) => r.value === choice);
        if (choice === 0) {
            setTempEscalateOrder({
                ...tempEscalateOrder,
                escalationReason: '',
            });
            return;
        }
        setTempEscalateOrder({
            ...tempEscalateOrder,
            escalationReason: reason!.text,
        });
    };
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        const name = e.target.name;
        //console.log('in on change text area', name, value);
        setTempEscalateOrder({
            ...tempEscalateOrder,
            [name]: value,
        });
    };
    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const name = e.target.name;
        //console.log('in on change input', name, value);
        setTempEscalateOrder({
            ...tempEscalateOrder,
            [name]: value,
        });
    };
    const onSave = () => {
        if (!formIsValid()) {
            return;
        }
        gaEvent(
            'Estore_Order_Updates',
            'Escalate_Order',
            order.billTo.accountNumber
        );

        escalateOrder(tempEscalateOrder);
    };

    const formIsValid = (): boolean => {
        const errors: errors = validate(tempEscalateOrder, fieldValidations);

        setErrorsState(errors);

        return Object.keys(errors).length === 0;
    };
    return (
        <Modal>
            <>
                {!success && !error && !confirmed && (
                    <>
                        <div className="modal-header">
                            <button
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label={intl.formatMessage({
                                    id: 'Close',
                                })}
                                onClick={onClose}
                            >
                                <span>&times;</span>
                            </button>
                            <h2 className="noSpacing">
                                <FormattedMessage
                                    id="Escalate_to_Customer_Service"
                                    defaultMessage="Escalate to Customer Service"
                                />
                            </h2>
                        </div>

                        <div className="modal-body-main">
                            <div className="text-center">
                                <p>
                                    <FormattedMessage
                                        id="We're_sorry_to_hear_escalate_modal"
                                        defaultMessage="We’re sorry to hear that you’re having
                                    troubles with your order. Please verify the
                                    info below and provide us with any
                                    additional details so we can contact you and
                                    try to resolve the issue."
                                    />
                                </p>
                            </div>

                            <div
                                className="row"
                                style={{
                                    paddingBottom: '20px',
                                }}
                            >
                                <div className="col-sm-12">
                                    <label className="control-label">
                                        <strong>
                                            <FormattedMessage
                                                id="Please_select_a_reason"
                                                defaultMessage="Please select a reason"
                                            />
                                        </strong>
                                        <span className="required"></span>
                                    </label>
                                    <SelectInputModal
                                        className="form-control"
                                        name="escalationReason"
                                        value={selected}
                                        onChange={onSelect}
                                        error={errorsState.escalationReason}
                                        options={options.map(
                                            (option: IOptions) => ({
                                                value: option.value,
                                                text: option.text,
                                            })
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-3">
                                    <label>
                                        <FormattedMessage
                                            id="Type"
                                            defaultMessage="Type"
                                        />
                                    </label>
                                </div>
                                <div className="col-sm-9">{orderType}</div>
                            </div>

                            <div className="row">
                                <div className="col-sm-3">
                                    <label>
                                        <FormattedMessage
                                            id="Items"
                                            defaultMessage="Items"
                                        />
                                    </label>
                                </div>
                                <div className="col-sm-9">
                                    {order.orderType}
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-3">
                                    <label>
                                        <FormattedMessage
                                            id="Order_Number_2"
                                            defaultMessage="Order Number"
                                        />
                                    </label>
                                </div>
                                <div className="col-sm-9">
                                    <p>{order.orderNumber}</p>
                                </div>
                            </div>

                            <div className="row">
                                {order.patientName && (
                                    <>
                                        <div className="col-sm-3">
                                            <label>
                                                <FormattedMessage
                                                    id="Patient_Name"
                                                    defaultMessage="Patient Name"
                                                />
                                            </label>
                                        </div>
                                        <div className="col-sm-9">
                                            <p>{order.patientName}</p>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="row">
                                <div className="col-sm-3">
                                    <label>
                                        <FormattedMessage
                                            id="Bill_to"
                                            defaultMessage="Bill To"
                                        />
                                    </label>
                                </div>
                                <div className="col-sm-9">
                                    <p>
                                        {accountDisplay(order.billTo)}
                                        <br />
                                        {billDisplay(order.billTo)}
                                    </p>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-3">
                                    <label>
                                        <FormattedMessage
                                            id="Ship_to"
                                            defaultMessage="Ship To"
                                        />
                                    </label>
                                </div>
                                <div className="col-sm-9">
                                    <p>
                                        {accountDisplay(order.shipTo)}
                                        <br />
                                        {shippingDisplay(order)}
                                    </p>
                                </div>
                            </div>

                            <>
                                {flattenedArray && (
                                    <>
                                        {flattenedArray.length !== 0 && (
                                            <div className="row">
                                                <div className="col-sm-3">
                                                    <label>
                                                        <FormattedMessage
                                                            id="Serial_Numbers"
                                                            defaultMessage="Serial Numbers"
                                                        />
                                                    </label>
                                                </div>
                                                <div className="col-sm-9">
                                                    {flattenedArray.map((i) => (
                                                        <p key={i}>{i}</p>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>

                            <div
                                className="row"
                                style={{ marginBottom: '15px' }}
                            >
                                <div className="col-sm-3">
                                    <label>
                                        <FormattedMessage
                                            id="Contact_Name"
                                            defaultMessage="Contact Name"
                                        />
                                        <span className="required"></span>
                                    </label>
                                </div>
                                <div className="col-sm-9">
                                    <TextInputLabelOptional
                                        name="contactName"
                                        placeholder="First_Last"
                                        value={tempEscalateOrder.contactName}
                                        error={errorsState.contactName}
                                        onChange={handleChangeInput}
                                    />
                                </div>
                            </div>
                            <div
                                className="row"
                                style={{ marginBottom: '15px' }}
                            >
                                <div className="col-sm-3">
                                    <label>
                                        <FormattedMessage
                                            id="Contact_Phone"
                                            defaultMessage="Contact Phone"
                                        />
                                        <span className="required"></span>
                                    </label>
                                </div>
                                <div className="col-sm-9">
                                    <TextInputLabelOptional
                                        name="contactInformation"
                                        placeholder="Phone_Number"
                                        value={
                                            tempEscalateOrder.contactInformation
                                        }
                                        error={errorsState.contactInformation}
                                        onChange={handleChangeInput}
                                    />
                                </div>
                            </div>
                            <div
                                className="row"
                                style={{ marginBottom: '15px' }}
                            >
                                <div className="col-sm-3">
                                    <label>
                                        <FormattedMessage
                                            id="Contact_Email"
                                            defaultMessage="Contact Email"
                                        />
                                        <span className="required"></span>
                                    </label>
                                </div>
                                <div className="col-sm-9">
                                    <TextInputLabelOptional
                                        name="contactEmail"
                                        placeholder="abc@email.com"
                                        value={tempEscalateOrder.contactEmail}
                                        error={errorsState.contactEmail}
                                        onChange={handleChangeInput}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-12">
                                    <label>
                                        <FormattedMessage
                                            id="Details"
                                            defaultMessage="Details"
                                        />
                                        <span className="required"></span>
                                    </label>
                                </div>
                                <div className="col-sm-12">
                                    <TextAreaLabelOptional
                                        name="customerInput"
                                        className="text-area"
                                        value={tempEscalateOrder.customerInput}
                                        error={errorsState.customerInput}
                                        onChange={handleChange}
                                        placeholder="Please provide any details and additional information about the issue"
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {!success && error && (
                    <>
                        <div className="modal-header">
                            <button
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label={intl.formatMessage({
                                    id: 'Close',
                                })}
                                onClick={() => {
                                    resetEscalationStatus();
                                    onClose();
                                }}
                            >
                                <span>&times;</span>
                            </button>
                            <h2 className="noSpacing">
                                <FormattedMessage
                                    id="Escalate_to_Customer_Service"
                                    defaultMessage="Escalate to Customer Service"
                                />
                            </h2>
                        </div>
                        <p className="text-center modal-spacing">
                            <FormattedMessage
                                id="Escalate_order_error_message"
                                defaultMessage="We're sorry there's been an error escalating this
                            order, please try again."
                            />
                        </p>
                    </>
                )}
                {success && !error && (
                    <>
                        <div className="modal-header">
                            <button
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label={intl.formatMessage({
                                    id: 'Close',
                                })}
                                onClick={() => {
                                    resetEscalationStatus();
                                    onClose();
                                }}
                            >
                                <span>&times;</span>
                            </button>
                            <h2 className="noSpacing">
                                <FormattedMessage
                                    id="Thank_you_for_notifying_us"
                                    defaultMessage="Thank you for notifying us."
                                />
                            </h2>
                        </div>
                        <p className="text-center">
                            <FormattedMessage
                                id="A_customer_service_rep"
                                defaultMessage="A customer service representative will be in contact
                            with you."
                            />
                        </p>
                    </>
                )}
                <div className="modal-footer">
                    {!success && !error && (
                        <>
                            <button
                                type="button"
                                className="btn btn-default"
                                onClick={onClose}
                            >
                                <FormattedMessage
                                    id="Cancel"
                                    defaultMessage="Cancel"
                                />
                            </button>
                            <button className="btn btn-info" onClick={onSave}>
                                <FormattedMessage
                                    id="Submit"
                                    defaultMessage="Submit"
                                />
                            </button>
                        </>
                    )}
                    {!success && error && (
                        <>
                            <button
                                name="add-item-cancel-if-error"
                                type="button"
                                className="btn btn-default"
                                onClick={onClose}
                            >
                                <FormattedMessage
                                    id="Cancel"
                                    defaultMessage="Cancel"
                                />
                            </button>
                            <button
                                name="add-item-retry-button"
                                type="button"
                                className="btn btn-default"
                                onClick={() => {
                                    setError(false);
                                }}
                            >
                                <FormattedMessage
                                    id="Retry"
                                    defaultMessage="Retry"
                                />
                            </button>
                        </>
                    )}
                    {success && !error && (
                        <button
                            name="add-item-close"
                            type="button"
                            className="btn btn-default"
                            onClick={() => {
                                resetEscalationStatus();
                                loadOrder(order.orderNumber);
                                onClose();
                            }}
                        >
                            <FormattedMessage
                                id="Close"
                                defaultMessage="Close"
                            />
                        </button>
                    )}
                </div>
            </>
        </Modal>
    );
};
const mapStateToProps = (state: RootState) => {
    return {
        escalateResponse: state.postEscalateResponse
            ? state.postEscalateResponse.data!
            : null,
    };
};

const mapDispatchToProps = {
    setIsBusy,
    loadOrder,
    escalateOrder,
    resetEscalationStatus,
};

const EscalateModalContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(EscalateModal);

export default EscalateModalContainer;
